import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, ThemeProvider, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { IInvoiceDate } from '@mopla/business-logic';
import { passengerThemeV2 } from '@mopla/mui';
import {
	BillIcon,
	DTicketPageLayout,
	EMlpIconTypes,
	EMplVariantTypes,
	MplBottomSheet,
	MplIconMessage,
	MplInvoiceListItem,
	MplListItem,
	MplTabs,
	StyledDTicketPageLayoutFooter,
} from '@mopla/ui';

import { usePassengerAppNavigate } from '../../../hooks';

import { StyledDownloadInvoicesListWrapper } from './DownloadInvoices.styled';
import { DownloadInvoicesSkeleton } from './DownloadInvoicesSkeleton';
import { useDownloadInvoices } from './useDownloadInvoices';

export const DownloadInvoices = () => {
	const { t } = useTranslation('downloadInvoices');
	const { navigateToUserDTicket, navigateToManageBillingAddress } =
		usePassengerAppNavigate();
	const {
		isLoading,
		isRateLimited,
		tabs,
		selectedYear,
		invoicesForYear,
		handleYearChange,
		sendInvoice,
	} = useDownloadInvoices();

	const formatInvoiceTitle = useCallback(
		(invoice: IInvoiceDate) => {
			const date = dayjs({ year: invoice.year, month: invoice.month - 1 });
			return `${t('invoice_prefix')} - ${date.format('MMMM YYYY')}`;
		},
		[t]
	);

	return (
		<ThemeProvider theme={passengerThemeV2}>
			<DTicketPageLayout
				title={t('page_title')}
				onBack={navigateToUserDTicket}
				scrollable={false}
				dataTestId="download-invoices-page"
			>
				{isLoading ? (
					<DownloadInvoicesSkeleton />
				) : (
					<Stack gap={2} sx={{ flex: 1, minHeight: 0 }}>
						<Stack gap={0.5}>
							<Typography variant="h3-sb" color="primary.main">
								{t('header_title')}
							</Typography>
							<Typography variant="b2" color="solutions.600">
								{t('header_description')}
							</Typography>
						</Stack>

						{tabs.length > 1 && (
							<MplTabs
								tabs={tabs}
								value={selectedYear}
								onChange={handleYearChange}
								dataTestId="download-invoices-year-tabs"
								scrollable={tabs.length > 3}
							/>
						)}

						<StyledDownloadInvoicesListWrapper>
							{invoicesForYear.length > 0 ? (
								invoicesForYear.map((invoice) => (
									<MplInvoiceListItem
										key={`${invoice.year}-${invoice.month}-${invoice.subscriptionId}`}
										title={formatInvoiceTitle(invoice)}
										onSendInvoiceClick={() => sendInvoice(invoice)}
										data-testid={`invoice-item-${invoice.year}-${invoice.month}`}
									/>
								))
							) : (
								<Typography
									variant="b2"
									color="solutions.600"
									textAlign="center"
								>
									{t('no_invoices')}
								</Typography>
							)}
						</StyledDownloadInvoicesListWrapper>
					</Stack>
				)}
			</DTicketPageLayout>
			{!isLoading && (
				<StyledDTicketPageLayoutFooter>
					<MplListItem
						text={t('manage_billing_address')}
						onClick={navigateToManageBillingAddress}
						leftIcon={<BillIcon />}
					/>
				</StyledDTicketPageLayoutFooter>
			)}
			<MplBottomSheet
				open={isRateLimited}
				isStatic
				testId="rate-limit-notification"
			>
				<Stack sx={{ px: 2, pt: 2 }}>
					<MplIconMessage
						type={EMlpIconTypes.Warning}
						variant={EMplVariantTypes.Error}
						message={t('rate_limit_error')}
					/>
				</Stack>
			</MplBottomSheet>
		</ThemeProvider>
	);
};
